import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, BrainCircuit, Zap, Loader2, X } from "lucide-react";
import request from "../../utils/request";
import PhoneDemo from "../PhoneDemo";

const DISMISS_KEY = "concierge_promo_dismissed";

const PHONE_SCALE = 190 / PhoneDemo.WIDTH;

export default function ConciergePromoCard({ hostId }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "true"
  );

  useEffect(() => {
    if (!hostId || dismissed) return;
    request
      .get(`/hosts/${hostId}/concierge/status`)
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ conciergeEnabled: false, status: "none" }))
      .finally(() => setLoading(false));
  }, [hostId, dismissed]);

  const handleSubscribe = async () => {
    setActionLoading(true);
    try {
      const { data } = await request.post(
        `/hosts/${hostId}/concierge/subscribe`,
        { tier: "basic" }
      );
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to start checkout");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "true");
    setDismissed(true);
  };

  if (dismissed || loading || status?.conciergeEnabled) return null;

  const plan = status?.plans?.basic;
  const priceLabel = plan ? `$${(plan.priceInCents / 100).toFixed(0)}` : "$100";

  return (
    <div
      style={{
        position: "relative",
        background: "#111827",
        borderRadius: 20,
        overflow: "hidden",
        marginBottom: 20,
      }}
    >
      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          zIndex: 10,
          width: 28,
          height: 28,
          borderRadius: 14,
          border: "none",
          background: "rgba(255,255,255,0.1)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        aria-label="Dismiss"
      >
        <X size={14} color="rgba(255,255,255,0.6)" />
      </button>

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 0,
        }}
      >
        {/* Left: copy */}
        <div style={{ flex: 1, padding: "36px 40px" }}>
          <p
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "#9ca3af",
              margin: "0 0 14px 0",
            }}
          >
            Concierge
          </p>

          <h2
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: "#fff",
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              margin: "0 0 12px 0",
            }}
          >
            Give your guests an experience
            <br />
            they'll remember.
          </h2>

          <p
            style={{
              fontSize: 14,
              color: "#9ca3af",
              lineHeight: 1.6,
              margin: "0 0 20px 0",
              maxWidth: 420,
            }}
          >
            Offer private chefs, spa, grocery delivery, and more — all bookable
            from a mobile app. Bellhopt's ConciergeAI handles guest questions so you don't
            have to.
          </p>

          {/* Benefit chips — single compact row */}
          <div
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 24,
              flexWrap: "wrap",
            }}
          >
            <Chip icon={Sparkles} label="Delight Guests" />
            <Chip icon={BrainCircuit} label="ConciergeAI" />
            <Chip icon={Zap} label="Zero Extra Work" />
          </div>

          {/* CTA row */}
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <button
              onClick={handleSubscribe}
              disabled={actionLoading}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "11px 28px",
                background: "#fff",
                color: "#111827",
                fontSize: 13,
                fontWeight: 700,
                borderRadius: 999,
                border: "none",
                cursor: "pointer",
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              {actionLoading && (
                <Loader2
                  size={14}
                  style={{ marginRight: 6 }}
                  className="animate-spin"
                />
              )}
              Enable Concierge — {priceLabel}/mo
            </button>
            <button
              onClick={() => navigate("/concierge-subscription")}
              style={{
                background: "none",
                border: "none",
                fontSize: 13,
                fontWeight: 500,
                color: "#9ca3af",
                cursor: "pointer",
                padding: 0,
              }}
            >
              Learn more
            </button>
          </div>
        </div>

        {/* Right: iPhone */}
        <div
          style={{
            flexShrink: 0,
            width: 260,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingRight: 32,
          }}
        >
          <div
            style={{
              width: 200,
              height: 408,
              borderRadius: 34,
              background: "#000",
              border: "1px solid rgba(255,255,255,0.1)",
              position: "relative",
              boxShadow:
                "0 20px 50px rgba(0,0,0,0.4), 0 8px 20px rgba(0,0,0,0.25)",
              overflow: "hidden",
            }}
          >
            {/* Dynamic Island */}
            <div
              style={{
                position: "absolute",
                top: 7,
                left: "50%",
                transform: "translateX(-50%)",
                width: 60,
                height: 18,
                borderRadius: 9,
                background: "#000",
                zIndex: 20,
              }}
            />
            {/* Screen */}
            <div
              style={{
                position: "absolute",
                top: 4,
                left: 4,
                right: 4,
                bottom: 4,
                borderRadius: 30,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <div
                style={{
                  width: PhoneDemo.WIDTH,
                  height: PhoneDemo.HEIGHT,
                  transform: `scale(${PHONE_SCALE})`,
                  transformOrigin: "top left",
                }}
              >
                <PhoneDemo />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Chip({ icon: Icon, label }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        borderRadius: 8,
        background: "rgba(255,255,255,0.07)",
        fontSize: 11,
        fontWeight: 600,
        color: "#e5e7eb",
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={13} color="#9ca3af" />
      {label}
    </span>
  );
}
